import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import useAPI from "hooks/useAPI";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Dashboard 2 React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftInput from "components/SoftInput";
import { formatDateWithFormat } from "utils/utilities";
import useConstants from "constants";
import Loader from "components/loader";
import AlertMessage from "components/UI/AlertMessage";
import useAlert from "hooks/useAlert";
import Stack from "@mui/material/Stack";
import { t } from "i18next";
import Backdrop from "@mui/material/Backdrop";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { Visibility, Refresh, SettingsOutlined } from "@mui/icons-material";
import LogDetails from "layouts/activity_log/LogDetails";
import { FormControl, InputLabel, Select, MenuItem, OutlinedInput } from "@mui/material";
import PropTypes from "prop-types";
import SoftButton from "components/SoftButton";
import MBConfirmationPopup from "components/MBConfirmationPopup";
import Unauthorized from "components/UI/Unauthorized";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { usePermissions } from "context/PermissionsContext";
import WithPermission from "components/WithPermission";
import { TableHeading } from "components/TableComponents";
import SortIcon from "components/SortIcon";
import MBDrawer from "components/MBDrawer";
import PageSettings from "components/PageSettings";
import moment from "moment";

const ActionType = ({ actiontype, children }) => (
  <SoftBox
    sx={{
      fontWeight: "bold",
      color:
        actiontype === "Read"
          ? "info.main"
          : actiontype === "Create"
          ? "success.main"
          : actiontype === "Update"
          ? "warning.main"
          : actiontype === "Delete"
          ? "error.main"
          : "text.primary",
    }}
  >
    {children}
  </SoftBox>
);
ActionType.propTypes = {
  actiontype: PropTypes.string.isRequired,
  children: PropTypes.string,
};

const ActivityLog = () => {
  const { errorState, showAlert } = useAlert();
  const [data, setData] = useState([]);
  const { per_page, items_per_page_options, date_time_format, date_format } = useConstants();
  const [loading, setLoading] = useState(false);
  const [searchLoaded, setSearchLoaded] = useState(false);
  const [totalRows, setTotalRows] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const { api } = useAPI();

  const [backdrop, setBackdrop] = useState(false);

  const activity_log_table_id = "activity-log";
  const default_sort_column = "id";
  const default_sort_direction = "desc";
  const default_items_per_page = per_page;

  const [perPage, setPerPage] = useState(default_items_per_page);
  const [orderColumn, setOrderColumn] = useState(default_sort_column);
  const [orderDirection, setOrderDirection] = useState(default_sort_direction);
  const [defaultSortColumnFromSettings, setDefaultSortColumnFromSettings] =
    useState(default_sort_column);
  const [defaultSortDirectionFromSettings, setDefaultSortDirectionFromSettings] =
    useState(default_sort_direction);
  const [defaultItemsPerPageFromSettings, setDefaultItemsPerPageFromSettings] =
    useState(default_items_per_page);
  const [rowId, setRowId] = useState(null);
  const [openLogDetailModel, setOpenLogDetailsModel] = useState(false);
  const [filterMethod, setFilterMethod] = useState("");
  const [filterSource, setFilterSource] = useState("");
  const [openConfirmPopup, setOpenConfirmPopup] = useState(false);
  const [filterByDate, setFilterByDate] = useState(null);
  const { hasPermission } = usePermissions();
  const [showUsername, setShowUsername] = useState(false);
  const [columnData, setColumnData] = useState([]);
  const [openSettingsDrawer, setOpenSettingsDrawer] = useState(false);
  const [tableId, setTableId] = useState(null);
  const [settingsSaveBtnLoading, setSettingsSaveBtnLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  if (!hasPermission("can_view_activitylog")) {
    return <Unauthorized />;
  }

  const fetchData = async (
    sortCol = null,
    sortDir = null,
    pageSize = null,
    isReset = false,
    searchValue = null,
    nextPage = null
  ) => {
    setLoading(true);
    let requestConfig = {};
    let params = {
      filterMethod: isReset ? "" : filterMethod,
      filterSource: isReset ? "" : filterSource,
      filterByDate: isReset ? null : filterByDate ? moment(filterByDate).format() : null,
    };
    if (!isInitialLoad && !isReset) {
      params = {
        page: nextPage || page,
        per_page: pageSize || perPage,
        search: searchValue,
        order_col: sortCol || orderColumn,
        order_dir: sortDir || orderDirection,
        ...params,
      };
    }
    requestConfig = { params };

    const response = await api.get(`/activity-log/`, requestConfig);
    const result = response.data;
    const page_settings = result.page_settings;
    setData(result.data);
    setTotalRows(result.recordsTotal);

    if (isInitialLoad || isReset) {
      if (page_settings && page_settings.sortColumn) {
        setOrderColumn(page_settings.sortColumn);
        setDefaultSortColumnFromSettings(page_settings.sortColumn);
      }
      if (page_settings && page_settings.sortDirection) {
        setOrderDirection(page_settings.sortDirection);
        setDefaultSortDirectionFromSettings(page_settings.sortDirection);
      }
      if (page_settings && page_settings.itemsPerPage) {
        setPerPage(page_settings.itemsPerPage);
        setDefaultItemsPerPageFromSettings(page_settings.itemsPerPage);
      }
      if (isReset && (!page_settings || Object.keys(page_settings).length === 0)) {
        setOrderColumn(default_sort_column);
        setOrderDirection(default_sort_direction);
        setPerPage(default_items_per_page);
        setDefaultSortColumnFromSettings(default_sort_column);
        setDefaultSortDirectionFromSettings(default_sort_direction);
        setDefaultItemsPerPageFromSettings(default_items_per_page);
      }
      setIsInitialLoad(false);
    }
    setLoading(false);
    setSearchLoaded(true);
    setShowUsername(result.show_username | false);
  };

  useEffect(() => {
    fetchData(orderColumn, orderDirection, perPage, false, search, page);
  }, [filterMethod, filterSource, filterByDate]);

  useEffect(() => {
    setColumnData(getColumns());
  }, [data]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchData(orderColumn, orderDirection, perPage, false, search, newPage);
  };

  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    setPage(1);

    fetchData(orderColumn, orderDirection, newPerPage, false, search, page);
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(1);
    fetchData(orderColumn, orderDirection, perPage, false, event.target.value, page);
  };

  const handleSort = (column, sortDirection) => {
    const newOrderColumn = column.columnKey;
    let newOrderDirection = sortDirection;
    if (newOrderColumn === orderColumn) {
      newOrderDirection = orderDirection === "asc" ? "desc" : "asc";
    }
    setOrderColumn(newOrderColumn);
    setOrderDirection(newOrderDirection);
    fetchData(newOrderColumn, newOrderDirection, perPage, false, search, page);
  };

  const alert = sessionStorage.getItem("alert");
  const alertError = sessionStorage.getItem("alertError");
  useEffect(() => {
    if (alert) {
      showAlert("success", alert);
      sessionStorage.removeItem("alert");
    }
    if (alertError) {
      showAlert("error", alertError);
      sessionStorage.removeItem("alertError");
    }
  }, [alert, alertError]);

  const handleOpenLogDetailDialog = (rowId) => {
    setRowId(rowId);
    setOpenLogDetailsModel(true);
  };

  const handleClearLogs = async () => {
    setOpenConfirmPopup(false);
    setLoading(true);
    try {
      const response = await api.delete("/activity-log/");
      if (response.status === 200) {
        showAlert("success", response?.data?.message);
        setData([]);
      }
    } catch (error) {
      showAlert("error", error.response?.data?.error);
    } finally {
      setLoading(false);
      setOpenConfirmPopup(false);
    }
  };
  const handleConfirmDelete = () => {
    handleClearLogs();
  };

  const handleOpenDeletePopup = () => {
    if (data.length > 0) {
      setOpenConfirmPopup(true);
    } else {
      return true;
    }
  };

  const getSortIcon = (columnKey) => {
    const isActive = orderColumn === columnKey;
    return <SortIcon orderDirection={orderDirection} isActive={isActive} />;
  };

  const getColumns = () => {
    const columns = [];
    columns.push({
      name: (
        <SoftBox display="flex" alignItems="center" gap={1}>
          <TableHeading text={t("lang.id")} />
          {getSortIcon("id")}
        </SoftBox>
      ),
      columnKey: "id",
      selector: (row) => row.id,
      sortable: true,
      width: "8%",
    });
    columns.push({
      name: (
        <SoftBox display="flex" alignItems="center" gap={1}>
          <TableHeading text={t("lang.action_type")} />
          {getSortIcon("action_type")}
        </SoftBox>
      ),
      columnKey: "action_type",
      cell: (row) => {
        return <ActionType actiontype={row.action_type}>{row.action_type}</ActionType>;
      },
      sortable: true,
      width: "10%",
    });
    columns.push({
      name: (
        <SoftBox display="flex" alignItems="center" gap={1}>
          <TableHeading text={t("lang.source_type")} />
          {getSortIcon("source_type")}
        </SoftBox>
      ),
      columnKey: "source_type",
      selector: (row) => {
        if (row.source_type === "User Based" || row.source_type === "Role Based") {
          return `${row.source_type} (${row.channel_name})`;
        } else {
          return row.source_type;
        }
      },
      sortable: true,
      width: "15%",
    });
    if (showUsername) {
      columns.push({
        name: (
          <SoftBox display="flex" alignItems="center" gap={1}>
            <TableHeading text={t("lang.user_name")} />
            {getSortIcon("user_name")}
          </SoftBox>
        ),
        columnKey: "user_name",
        selector: (row) => row.user_name || "",
        sortable: true,
        width: "15%",
      });
    }
    columns.push({
      name: (
        <SoftBox display="flex" alignItems="center" gap={1}>
          <TableHeading text={t("lang.description")} />
          {getSortIcon("description")}
        </SoftBox>
      ),
      columnKey: "description",
      selector: (row) => row.description,
      sortable: true,
      width: showUsername ? "27%" : "43%",
    });
    columns.push({
      name: (
        <SoftBox display="flex" alignItems="center" gap={1}>
          <TableHeading text={t("lang.action_datetime")} />
          {getSortIcon("action_datetime")}
        </SoftBox>
      ),
      columnKey: "action_datetime",
      selector: (row) => formatDateWithFormat(row.action_datetime, date_time_format),
      sortable: true,
      width: "15%",
    });
    // if (hasPermission("user.can_edit") || hasPermission("user.can_delete")) {
    columns.push({
      name: (
        <SoftBox display="flex" alignItems="center" gap={1}>
          <TableHeading text={t("lang.actions.action")} />
        </SoftBox>
      ),
      selector: (row) => {
        return (
          <Stack
            direction="row"
            sx={{
              flexWrap: "nowrap",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Tooltip title={t("lang.view_log")} placement="top">
              <IconButton aria-label="view" onClick={() => handleOpenLogDetailDialog(row.id)}>
                <Visibility color="secondary" />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      },
      sortable: false,
      width: "8%",
    });
    // }
    return columns;
  };

  const handleSettingsClick = (tableId) => {
    setTableId(tableId);
    setOpenSettingsDrawer(true);
  };

  const handleSettingsChange = async (settings) => {
    const newSettings = settings[tableId];
    setSettingsSaveBtnLoading(true);
    try {
      const response = await api.post(`/user-page-settings/`, {
        page_slug: "activity-log",
        settings: settings,
      });
      if (response.status == 200) {
        // Apply the new settings
        setOrderColumn(newSettings.sortColumn);
        setOrderDirection(newSettings.sortDirection);
        setPerPage(newSettings.itemsPerPage);
        setDefaultSortColumnFromSettings(newSettings.sortColumn);
        setDefaultSortDirectionFromSettings(newSettings.sortDirection);
        setDefaultItemsPerPageFromSettings(newSettings.itemsPerPage);
        setPage(1); // Reset to first page

        // Fetch data with new settings
        fetchData(
          newSettings.sortColumn,
          newSettings.sortDirection,
          newSettings.itemsPerPage,
          false,
          search,
          1
        );
        setOpenSettingsDrawer(false);
        setTableId(null);

        showAlert("success", response.data.message);
      } else {
        showAlert("error", response.data.error);
      }
    } catch (err) {
      showAlert("error", err?.response?.data?.error || "");
    } finally {
      setSettingsSaveBtnLoading(false);
    }
  };

  const handleResetTable = async () => {
    setSearch("");
    setFilterMethod("");
    setFilterSource("");
    setFilterByDate(null);
    fetchData(orderColumn, orderDirection, perPage, true, "", 1);
  };

  const pagesList = [
    {
      name: t("lang.activity_log"),
      route: "",
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar pagesList={pagesList} />
      <MBConfirmationPopup
        open={openConfirmPopup}
        handleClose={() => setOpenConfirmPopup(false)}
        handleConfirm={handleConfirmDelete}
        title={t("lang.delete_confirmation")}
        message={t("lang.are_you_sure_to_delete_logs")}
        confirmText={t("lang.yes_delete")}
        cancelText={t("lang.no_cancel")}
      />
      <LogDetails
        title={t("lang.log_deatails")}
        openModel={openLogDetailModel}
        closeModel={() => setOpenLogDetailsModel(false)}
        rowId={rowId}
      />

      <Backdrop
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(255, 253, 253, 0.71)",
          zIndex: 1,
        }}
        open={backdrop}
      >
        <Loader color="inherit" />
      </Backdrop>
      {/* Settings Drawer */}
      <MBDrawer
        open={openSettingsDrawer}
        onClose={() => setOpenSettingsDrawer(false)}
        title={t("lang.settings")}
        anchor="right"
        height="100vh"
        width="400px"
      >
        <PageSettings
          saveBtnLoading={settingsSaveBtnLoading}
          tableId={tableId}
          defaultSortColumn={defaultSortColumnFromSettings}
          defaultSortDirection={defaultSortDirectionFromSettings}
          defaultItemsPerPage={defaultItemsPerPageFromSettings}
          sortableColumns={[
            { value: "id", label: t("lang.id") },
            { value: "action_type", label: t("lang.action_type") },
            { value: "source_type", label: t("lang.source_type") },
            { value: "user_name", label: t("lang.user_name") },
            { value: "description", label: t("lang.description") },
            { value: "action_datetime", label: t("lang.action_datetime") },
          ]}
          onSettingsChange={handleSettingsChange}
        />
      </MBDrawer>
      <SoftBox>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <WithPermission permission="can_delete_activitylog">
              <SoftBox display="flex" justifyContent="flex-end" m={2}>
                <SoftButton
                  type="button"
                  variant="contained"
                  color="error"
                  onClick={handleOpenDeletePopup}
                >
                  {t("lang.clear_log")}
                </SoftButton>
              </SoftBox>
            </WithPermission>
            <Card>
              <SoftBox p={3}>
                <Grid container spacing={2} p={2}>
                  {/* First Dropdown */}
                  <Grid item xs={12} md={8}>
                    <Stack direction="row" spacing={2}>
                      <SoftBox width="12rem">
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                          <SoftTypography variant="overline" gutterBottom>
                            {t("lang.filter_by_date")}
                          </SoftTypography>
                          <FormControl fullWidth>
                            <DatePicker
                              value={filterByDate}
                              onChange={(newValue) => {
                                setFilterByDate(newValue);
                              }}
                              format={date_format}
                              slotProps={{
                                textField: {
                                  clearable: true,
                                  size: "small",
                                  sx: {
                                    "& .MuiInputAdornment-root": {
                                      position: "absolute",
                                      right: "10px",
                                      top: "50%",
                                      transform: "translateY(-50%)",
                                      height: "auto",
                                      margin: 0,
                                      pointerEvents: "none",
                                    },
                                    // The calendar icon itself
                                    "& .MuiIconButton-root": {
                                      padding: "4px",
                                      pointerEvents: "auto", // allow clicking icon
                                    },
                                    "& .MuiSvgIcon-root": {
                                      fontSize: "1.2rem",
                                    },
                                    "& .clearButton": {
                                      marginRight: "20px",
                                    },
                                    "& .MuiInputBase-root": {
                                      padding: "0.6rem",
                                    },
                                  },
                                },
                              }}
                            />
                          </FormControl>
                        </LocalizationProvider>
                      </SoftBox>
                      <SoftBox width="12rem">
                        <FormControl fullWidth>
                          <SoftTypography variant="overline" gutterBottom>
                            {t("lang.filter_by_method")}
                          </SoftTypography>
                          <Select
                            value={filterMethod}
                            onChange={(e) => setFilterMethod(e.target.value)}
                            fullWidth
                            input={<OutlinedInput sx={{ height: 56 }} />}
                            sx={{ padding: "10px" }}
                          >
                            <MenuItem value="">{t("lang.all")}</MenuItem>
                            <MenuItem value="1">{t("lang.create")}</MenuItem>
                            <MenuItem value="2">{t("lang.read")}</MenuItem>
                            <MenuItem value="3">{t("lang.update")}</MenuItem>
                            <MenuItem value="4">{t("lang.delete")}</MenuItem>
                          </Select>
                        </FormControl>
                      </SoftBox>
                      <SoftBox width="12rem">
                        <FormControl fullWidth>
                          <SoftTypography variant="overline" gutterBottom>
                            {t("lang.filter_by_source")}
                          </SoftTypography>
                          <Select
                            value={filterSource}
                            onChange={(e) => setFilterSource(e.target.value)}
                            fullWidth
                            input={<OutlinedInput sx={{ height: 56 }} />}
                            sx={{ padding: "10px" }}
                          >
                            <MenuItem value="">{t("lang.all")}</MenuItem>
                            <MenuItem value="1">{t("lang.web")}</MenuItem>
                            <MenuItem value="2">{t("lang.role_based")}</MenuItem>
                            <MenuItem value="3">{t("lang.user_based")}</MenuItem>
                          </Select>
                        </FormControl>
                      </SoftBox>
                    </Stack>
                  </Grid>

                  {/* Search Input (unchanged) */}
                  <Grid item xs={12} md={4}>
                    <Stack alignItems="flex-end">
                      <SoftBox width="12rem">
                        <SoftTypography
                          variant="overline"
                          gutterBottom
                          sx={{ visibility: "hidden" }}
                        >
                          {t("lang.actions.search")}
                        </SoftTypography>
                      </SoftBox>
                      {searchLoaded && (
                        <SoftBox
                          display="flex"
                          alignItems="center"
                          gap={1}
                          sx={{
                            width: {
                              xs: "100%", // Full width on extra-small screens (mobile)
                              sm: "12rem", // Fixed width on small and up
                              md: "20rem",
                            },
                            ml: "auto",
                            mb: "1rem",
                          }}
                        >
                          <Tooltip title={t("lang.settings")} arrow placement="top">
                            <SoftButton
                              variant="outlined"
                              color="secondary"
                              onClick={() => handleSettingsClick(activity_log_table_id)}
                              size="small"
                              sx={{
                                padding: "0.45rem 1rem",
                                "& svg": {
                                  fontSize: "24px !important",
                                },
                              }}
                            >
                              <SettingsOutlined color="secondary" />
                            </SoftButton>
                          </Tooltip>
                          <Tooltip title={t("lang.reset")} arrow placement="top">
                            <SoftButton
                              variant="outlined"
                              color="secondary"
                              onClick={() => handleResetTable()}
                              size="small"
                              sx={{
                                padding: "0.45rem 1rem",
                                "& svg": {
                                  fontSize: "24px !important",
                                },
                              }}
                            >
                              <Refresh color="secondary" />
                            </SoftButton>
                          </Tooltip>
                          <SoftBox width="12rem" ml="auto">
                            <SoftInput
                              placeholder={t("lang.actions.search")}
                              size="small"
                              fullWidth
                              value={search}
                              onChange={handleSearchChange}
                              sx={{ padding: "8px" }}
                            />
                          </SoftBox>
                        </SoftBox>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
                <DataTable
                  className={activity_log_table_id}
                  key={`${orderColumn}-${orderDirection}-${perPage}-${data.length}`}
                  columns={columnData}
                  data={data}
                  progressPending={loading}
                  progressComponent={<Loader />}
                  pagination
                  paginationServer
                  paginationTotalRows={totalRows}
                  onChangeRowsPerPage={handlePerPageChange}
                  onChangePage={handlePageChange}
                  onSort={handleSort}
                  sortServer
                  paginationPerPage={perPage}
                  paginationRowsPerPageOptions={items_per_page_options}
                  highlightOnHover
                  paginationDefaultPage={page}
                  clearSelectedRows={true}
                  responsive={true}
                  selectableRows={false}
                  pointerOnHover={false}
                  fixedHeader={true}
                  persistTableHead={true}
                  sortIcon={() => {
                    return <></>;
                  }}
                />
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
      <AlertMessage {...errorState} />
    </DashboardLayout>
  );
};

export default ActivityLog;
